import React from 'react'
import {Alert, Button, Col, Container, FormControl, FormGroup, FormLabel, Row, Table} from 'react-bootstrap'
import './EventList.css'
import StyledSelect from "../../components/StyledSelect";
import {format, parseISO, subMonths, addMonths, startOfMonth} from "date-fns";
import sv from "date-fns/locale/sv";
import {BsExclamationCircleFill, BsEye} from "react-icons/bs";
import EventDetailDialog from "./EventDetailDialog";
import {eventTypeLabel, eventTypes} from "./EventTypeLabels";
import PaginationControls from "../../components/PaginationControls";

const EventList = ({
                       events,
                       careUnits,
                       opticians,
                       filter,
                       updateEventListFilter,
                       errorMessage,
                       selectedEvent,
                       detailDialogOpen,
                       openEventDetailDialog,
                       closeEventDetailDialog,
                       currentPage,
                       totalPages,
                       totalElements,
                       statusCounts,
                       onPageChange
                   }) => {

    const fromInputRef = React.createRef();
    const beforeInputRef = React.createRef();

    const onFilterChange = (overrides = {}) => {
        updateEventListFilter({
            ...filter,
            from: fromInputRef.current?.value ?? filter.from,
            before: beforeInputRef.current?.value ?? filter.before,
            ...overrides,
        });
    }

    const setFilterMonth = (date) => {
        fromInputRef.current.value = format(startOfMonth(date), 'yyyy-MM-dd');
        beforeInputRef.current.value = format(startOfMonth(addMonths(date, 1)), 'yyyy-MM-dd');
        onFilterChange();
    }

    return (
        <div className="EventList">
            <Container id={"eventMainGrid"}>
                {errorMessage &&
                    <Row>
                        <Col xs={12} md={12} lg={12}>
                            <Alert variant={"danger"}>
                                <b><BsExclamationCircleFill/> {errorMessage}</b>
                            </Alert>
                        </Col>
                    </Row>
                }
                <Row>
                    <Col className="mb-1" xs={12} md={12} lg={12}>
                        <h1>Händelser</h1>
                    </Col>
                </Row>
                <Row>
                    <Col className="mb-xs-3" xs={12} sm={6} md={6} lg={3}>
                        <FormGroup controlId="formHorizontalFrom">
                            <FormLabel>Från och med (inkl. valt datum)</FormLabel>
                            <FormControl type="date" name="fromDate" placeholder="Datum ÅÅÅÅ-MM-DD"
                                         ref={fromInputRef}
                                         onChange={() => onFilterChange()}
                                         onClick={(e) => e.target.showPicker()}
                                         defaultValue={filter.from ? filter.from : ""}
                            />
                        </FormGroup>
                    </Col>
                    <Col className="mb-xs-3" xs={12} sm={6} md={6} lg={3}>
                        <FormGroup controlId="formHorizontalBefore">
                            <FormLabel>Till (exkl. valt datum)</FormLabel>
                            <FormControl type="date" name="beforeDate" placeholder="Datum ÅÅÅÅ-MM-DD"
                                         ref={beforeInputRef}
                                         onChange={() => onFilterChange()}
                                         onClick={(e) => e.target.showPicker()}
                                         defaultValue={filter.before ? filter.before : ""}
                            />
                        </FormGroup>
                    </Col>
                    <Col className="mb-xs-3" xs={12} sm={6} md={6} lg={3}>
                        <FormGroup controlId="formHorizontalReceivingOrganization">
                            <FormLabel>Vårdenhet</FormLabel>
                            <StyledSelect
                                value={filter.careUnitOrganizationId || ""}
                                placeholder="Alla vårdenheter"
                                options={[
                                    {value: "", label: "Alla vårdenheter"},
                                    ...(careUnits || []).map(o => ({value: o.organizationId, label: o.name}))
                                ]}
                                onChange={(val) => onFilterChange({careUnitOrganizationId: val})}
                            />
                        </FormGroup>
                    </Col>
                    <Col className="mb-xs-3" xs={12} sm={6} md={6} lg={3}>
                        <FormGroup controlId="formHorizontalSendingOrganization">
                            <FormLabel>Optiker</FormLabel>
                            <StyledSelect
                                value={filter.opticianOrganizationId || ""}
                                placeholder="Alla optiker"
                                options={[
                                    {value: "", label: "Alla optiker"},
                                    ...(opticians || []).map(o => ({value: o.organizationId, label: o.name}))
                                ]}
                                onChange={(val) => onFilterChange({opticianOrganizationId: val})}
                            />
                        </FormGroup>
                    </Col>
                </Row>
                <Row className="mt-lg-3">
                    <Col xs={4} md={3} lg={3}>
                        <h2>Snabbval</h2>
                        <ul>
                            {Array.from({length: 6}).map((e, i) =>
                                <li key={i}><a href={""} onClick={(e) => {
                                    e.preventDefault();
                                    setFilterMonth(subMonths(new Date(), i))
                                }}>{format(subMonths(new Date(), i), "MMMM-yy", {locale: sv})}</a></li>
                            )}
                        </ul>
                    </Col>
                    <Col xs={5} md={6} lg={6}>
                        <h2>Sammanställning</h2>
                        <p>Totalt antal händelser: <b>{totalElements ?? 0}</b></p>
                        <p><b>Lyckade (200): {statusCounts?.success ?? 0}</b></p>
                        <p>Klientfel (4XX): {statusCounts?.clientError ?? 0}</p>
                        <p>Serverfel (5XX): {statusCounts?.serverError ?? 0}</p>
                    </Col>
                    <Col xs={3} md={3} lg={3}>
                        <FormGroup controlId="formHorizontalEventType">
                            <FormLabel>Händelsetyp</FormLabel>
                            <StyledSelect
                                value={filter.eventType || ""}
                                placeholder="Alla händelser"
                                options={[
                                    {value: "", label: "Alla händelser"},
                                    ...eventTypes
                                ]}
                                onChange={(val) => onFilterChange({eventType: val})}
                            />
                        </FormGroup>
                    </Col>
                </Row>
            </Container>

            <Table size="sm" hover responsive className="event-table">
                <thead>
                <tr>
                    <th>#</th>
                    <th>Tid</th>
                    <th>Händelse</th>
                    <th>Användare</th>
                    <th>Beskrivning</th>
                    <th>Status</th>
                    <th></th>
                </tr>
                </thead>
                <tbody>
                {
                    events && events.map((event, index) =>
                        <tr key={index}>
                            <td>{currentPage * 25 + index + 1}</td>
                            <td>{format(parseISO(event.timestamp), 'yyyy-MM-dd HH:mm:ss')}</td>
                            <td>{eventTypeLabel(event.type)}</td>
                            <td>{event.userId}</td>
                            <td>{event.description || '-'}</td>
                            <td>{event.returnedStatusCode || '-'}</td>
                            <td>
                                <Button
                                    size="sm"
                                    variant="primary"
                                    onClick={() => openEventDetailDialog(event)}
                                >
                                    <BsEye/> Öppna
                                </Button>
                            </td>
                        </tr>
                    )
                }
                </tbody>
            </Table>

            <PaginationControls
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={onPageChange}
            />

            {detailDialogOpen && selectedEvent && (
                <EventDetailDialog
                    event={selectedEvent}
                    onClose={closeEventDetailDialog}
                />
            )}
        </div>)
}
export default EventList;